import React from "react";
import { AxiosError } from "axios";
import { ConfirmationModal } from "@packsize/components";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { FormattedMessage, useIntl } from "react-intl";
import { IconButton, Typography } from "@mui/material";

import { type Classification } from "@/schemas";
import { useDeleteClassification } from "@/hooks/classifications";

interface ClassificationDeleteButtonProps {
	readonly classification: Classification;
}

export const ClassificationDeleteButton: React.FC<ClassificationDeleteButtonProps> = ({ classification }) => {
	const intl = useIntl();

	const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

	const { mutate, isPending, error } = useDeleteClassification(classification.id);

	const handleDeleteConfirmed = () => {
		mutate(undefined, {
			onSuccess: () => {
				setShowDeleteConfirmation(false);
			},
			onError: (error) => {
				if (error instanceof AxiosError && error.status === 404) {
					setShowDeleteConfirmation(false);
				}
			},
		});
	};

	return (
		<>
			<ConfirmationModal
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				confirmText={intl.formatMessage({ id: "Delete", defaultMessage: "Delete" })}
				onCancel={() => setShowDeleteConfirmation(false)}
				onClose={() => setShowDeleteConfirmation(false)}
				onConfirm={handleDeleteConfirmed}
				open={showDeleteConfirmation}
				size="sm"
				title={intl.formatMessage(
					{
						id: "Delete {alias}?",
						defaultMessage: "Delete {alias}?",
					},
					{
						alias: classification.alias,
					},
				)}
				slotProps={{
					confirmButton: {
						"data-testid": "classification-delete-confirmation-modal__button__confirm",
						loading: isPending,
					},
					cancelButton: {
						"data-testid": "classification-delete-confirmation-modal__button__cancel",
					},
				}}
			>
				<Typography>
					<FormattedMessage
						id="Are you sure you want to delete this classification?"
						defaultMessage="Are you sure you want to delete this classification?"
					/>
				</Typography>
				<Typography>
					<FormattedMessage id="This action cannot be undone." defaultMessage="This action cannot be undone." />
				</Typography>

				{Boolean(error) && (
					<Typography sx={{ marginTop: 2 }} color="error" data-testid="classification-delete-confirmation-modal__error">
						<FormattedMessage
							id="Failed to delete classification. Please try again."
							defaultMessage="Failed to delete classification. Please try again."
						/>
					</Typography>
				)}
			</ConfirmationModal>
			<IconButton
				onClick={() => setShowDeleteConfirmation(true)}
				data-testid="classification-delete-button__button__open-modal"
			>
				<DeleteOutlineRoundedIcon />
			</IconButton>
		</>
	);
};
